import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { map, take, switchMap } from 'rxjs/operators';
import { AuthService } from './auth.service';

/**
 * Guard that protects routes requiring authentication
 * Initializes auth state before checking authentication
 */
export const authGuard = () => {
  const authService = inject(AuthService);
  const router = inject(Router);

  // Initialize auth state first, then check authentication
  return authService.init().pipe(
    switchMap(() => authService.getIsAuthenticated$()),
    take(1),
    map(isAuthenticated => {
      if (isAuthenticated) {
        return true;
      }
      // Redirect to login if not authenticated
      return router.createUrlTree(['/login']);
    })
  );
};

/**
 * Guard that protects public-only routes (like login/signup)
 * Redirects to todos (planner dashboard) if already authenticated
 * Initializes auth state before checking authentication
 */
export const publicOnlyGuard = () => {
  const authService = inject(AuthService);
  const router = inject(Router);

  // Initialize auth state first, then check authentication
  return authService.init().pipe(
    switchMap(() => authService.getIsAuthenticated$()),
    take(1),
    map(isAuthenticated => {
      if (isAuthenticated) {
        // If user is authenticated, redirect to planner dashboard
        return router.createUrlTree(['/todos']);
      }
      // Allow access to public routes
      return true;
    })
  );
};

/**
 * Guard for home route that redirects authenticated users to the planner dashboard
 * Allows unauthenticated users to see the marketing home page
 * Initializes auth state before checking authentication
 */
export const homeGuard = () => {
  const authService = inject(AuthService);
  const router = inject(Router);

  // Initialize auth state first, then check authentication
  return authService.init().pipe(
    switchMap(() => authService.getIsAuthenticated$()),
    take(1),
    map(isAuthenticated => {
      if (isAuthenticated) {
        // If user is authenticated, redirect to planner dashboard
        return router.createUrlTree(['/todos']);
      }
      // Allow unauthenticated users to access home
      return true;
    })
  );
};
